import { FC } from 'react'
import { SectionEmpty } from './Section'
import {
  ConfigParam,
  DiffRow,
  DiffStatus,
  HardwareSummary,
  MethodologyViewModel,
  SystemSummary,
  diffCount,
  diffParams,
  diffSteps
} from './methodologyDiff'

export type MethodologyConfigProps = {
  baseline?: MethodologyViewModel | null
  proposed?: MethodologyViewModel | null
}

const statusCellClass: Record<DiffStatus, string> = {
  same: '',
  changed: 'bg-amber-50 dark:bg-amber-900/20',
  added: 'bg-green-50 dark:bg-green-900/20',
  removed: 'bg-red-50 dark:bg-red-900/20'
}

const statusBadge: Record<DiffStatus, { label: string; cls: string } | null> = {
  same: null,
  changed: { label: 'changed', cls: 'bg-amber-100 text-amber-800' },
  added: { label: 'added', cls: 'bg-green-100 text-green-800' },
  removed: { label: 'removed', cls: 'bg-red-100 text-red-800' }
}

const DiffBadge: FC<{ status: DiffStatus }> = ({ status }) => {
  const badge = statusBadge[status]
  if (!badge) return null
  return (
    <span
      className={`ml-2 text-[9px] font-bold uppercase tracking-wide px-1 py-0.5 rounded ${badge.cls}`}>
      {badge.label}
    </span>
  )
}

const ParamDiffTable: FC<{ rows: DiffRow[] }> = ({ rows }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-xs border-collapse">
      <thead>
        <tr className="bg-gray-50 dark:bg-neutral-70">
          <th className="border border-gray-200 dark:border-neutral-60 px-2 py-1 text-left font-bold">
            Parameter
          </th>
          <th className="border border-gray-200 dark:border-neutral-60 px-2 py-1 text-left font-bold">
            Baseline
          </th>
          <th className="border border-gray-200 dark:border-neutral-60 px-2 py-1 text-left font-bold">
            Proposed
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr key={row.name} className={statusCellClass[row.status]}>
            <td className="border border-gray-200 dark:border-neutral-60 px-2 py-1">
              {row.name}
              <DiffBadge status={row.status} />
            </td>
            <td className="border border-gray-200 dark:border-neutral-60 px-2 py-1 tabular-nums">
              {row.baseValue ?? '—'}
            </td>
            <td className="border border-gray-200 dark:border-neutral-60 px-2 py-1 tabular-nums">
              {row.propValue ?? '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

const hardwareRows = (h: HardwareSummary): ConfigParam[] => [
  { name: 'Core type', value: h.coreType || '—' },
  { name: 'Slots', value: String(h.slots) },
  { name: 'Cores / slot', value: String(h.coresPerSlot) },
  { name: 'Total cores', value: String(h.totalCores) },
  { name: 'Walltime (h)', value: String(h.walltime) }
]

const systemsAsParams = (systems: SystemSummary[]): ConfigParam[] =>
  systems.map(s => ({
    name: s.name,
    value: [s.vendorVersion, s.branchName, s.commitId].filter(Boolean).join(' · ')
  }))

const SubHeading: FC<{ title: string; changes: number }> = ({ title, changes }) => (
  <div className="flex items-center gap-2 mt-4 mb-1 first:mt-0">
    <h4 className="text-xs font-bold text-gray-700 dark:text-gray-200">{title}</h4>
    {changes > 0 && (
      <span className="text-[10px] font-bold text-amber-700 dark:text-amber-300">
        {changes} change{changes === 1 ? '' : 's'}
      </span>
    )}
  </div>
)

/** Methodology configuration comparison + diff (STAR-CCM+ params, hardware, systems, steps). */
const MethodologyConfig: FC<MethodologyConfigProps> = ({ baseline, proposed }) => {
  if (!baseline && !proposed) return <SectionEmpty message="Select methodologies to compare." />
  if (!baseline || !proposed) {
    return (
      <SectionEmpty message="Select both a baseline and a proposed methodology to see the diff." />
    )
  }

  const paramRows = diffParams(baseline.starParams, proposed.starParams)
  const miscRows = diffParams(baseline.miscParams, proposed.miscParams)
  const hardwareRowsDiff = diffParams(
    hardwareRows(baseline.hardware),
    hardwareRows(proposed.hardware)
  )
  const systemRows = diffParams(
    systemsAsParams(baseline.systems),
    systemsAsParams(proposed.systems)
  )
  const stepRows = diffSteps(baseline.stepOrder, proposed.stepOrder)
  const stepChanges = diffCount(stepRows)

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        {[
          { label: 'Baseline methodology', vm: baseline },
          { label: 'Proposed methodology', vm: proposed }
        ].map(({ label, vm }) => (
          <div
            key={label}
            className="rounded border border-gray-200 dark:border-neutral-70 p-2 bg-gray-50 dark:bg-neutral-80">
            <div className="text-[10px] uppercase tracking-wide text-gray-400 font-bold">
              {label}
            </div>
            <div className="text-sm font-bold text-gm-blue">{vm.name || '—'}</div>
            {vm.description && <div className="text-xs text-gray-500">{vm.description}</div>}
            {vm.version && <div className="text-[10px] text-gray-400 mt-0.5">{vm.version}</div>}
          </div>
        ))}
      </div>

      <SubHeading title="STAR-CCM+ parameters" changes={diffCount(paramRows)} />
      <ParamDiffTable rows={paramRows} />

      <SubHeading title="Hardware" changes={diffCount(hardwareRowsDiff)} />
      <ParamDiffTable rows={hardwareRowsDiff} />

      <SubHeading title="Systems" changes={diffCount(systemRows)} />
      <ParamDiffTable rows={systemRows} />

      {miscRows.length > 0 && (
        <>
          <SubHeading title="Miscellaneous parameters" changes={diffCount(miscRows)} />
          <ParamDiffTable rows={miscRows} />
        </>
      )}

      <SubHeading title="Macro step order" changes={stepChanges} />
      <ol className="text-xs list-decimal list-inside space-y-0.5">
        {stepRows.map(s => (
          <li
            key={s.step}
            className={`px-1 rounded ${statusCellClass[s.status]} ${
              s.status === 'removed' ? 'line-through text-gray-400' : ''
            }`}>
            {s.step}
            <DiffBadge status={s.status} />
          </li>
        ))}
      </ol>
    </div>
  )
}

export default MethodologyConfig
